<!--
  Potential interest — opened from the People list (button next to "Mailings").

  Products whose PROJECTED stock is the heaviest (current stock + approved quotations not
  yet invoiced, exactly like the stock list), with the clients who bought them the most.
  Tick clients, then "Send an offer": one e-mail per client, in the client's language, at
  the XYLO-PRICING catalogue price of the client's zone (incoterm).
  "All potential clients" opens the per-product analysis (quotes without order, bought
  together, similar products, same area).
-->
<template>
  <div id="potential-interest">
    <vx-card class="mb-base">
      <div class="flex flex-wrap items-end">
        <div class="mr-4 mb-3">
          <label class="text-sm block">Sort by</label>
          <select class="pi-select" v-model="filters.sort" @change="load">
            <option value="volume">Projected volume (m³)</option>
            <option value="qty">Projected quantity</option>
            <option value="coverage">Stock coverage (months of sales)</option>
            <option value="buyers">Number of buyers</option>
          </select>
        </div>
        <div class="mr-4 mb-3" style="min-width: 260px">
          <label class="text-sm block">Product families</label>
          <v-select multiple :options="typeOptions" v-model="filters.types" :reduce="o => o.value" label="label"
            placeholder="All (raw material excluded)" @input="load" />
        </div>
        <div class="mr-4 mb-3" style="min-width: 260px">
          <label class="text-sm block">Categories</label>
          <v-select multiple :options="categoryOptions" v-model="filters.categories" :reduce="o => o.value" label="label"
            placeholder="All" @input="load" />
        </div>
        <div class="mr-4 mb-3">
          <label class="text-sm block">Min. projected m³</label>
          <input class="pi-input" type="number" min="0" step="1" v-model.number="filters.minVolume" @change="load" style="width: 110px" />
        </div>
        <div class="mr-4 mb-3">
          <label class="text-sm block">Products</label>
          <select class="pi-select" v-model.number="filters.limit" @change="load">
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
            <option :value="200">200</option>
          </select>
        </div>
        <vs-checkbox v-model="filters.onlyWithBuyers" class="mr-4 mb-4">Only products already sold</vs-checkbox>
        <vs-checkbox v-if="!filters.types.length" v-model="filters.includeRaw" class="mr-4 mb-4">Include raw material</vs-checkbox>
        <vs-button class="mb-3" icon-pack="feather" icon="icon-refresh-cw" type="border" @click="load">Refresh</vs-button>
      </div>
      <div class="text-sm opacity-75">
        Projected stock = current stock + approved quotations not invoiced yet (same figure as the stock list).
        Coverage = projected stock ÷ average monthly sales over the last 12 months.
      </div>
    </vx-card>

    <vs-alert v-if="error" color="danger" active="true" class="mb-4">{{ error }}</vs-alert>

    <div class="vx-row">
      <!-- Products -->
      <div class="vx-col w-full xl:w-3/5 mb-base">
        <vx-card :title="`Products (${products.length})`">
          <div v-if="loading" class="mb-2">Loading...</div>
          <div class="pi-scroll">
            <table class="pi-table w-full">
              <thead>
                <tr>
                  <th>Product</th>
                  <th class="text-right">Projected</th>
                  <th class="text-right">m³</th>
                  <th class="text-right">Sold 12 m</th>
                  <th class="text-right">Coverage</th>
                  <th class="text-right">Buyers</th>
                  <th>Top buyers</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in products" :key="p.idproduct" class="pi-product-row"
                  :class="{ 'pi-product-row--active': current && current.idproduct === p.idproduct }" @click="pick(p)">
                  <td>
                    <div class="font-semibold">{{ p.product }}</div>
                    <div class="text-xs opacity-75">{{ p.category }} · #{{ p.idproduct }}</div>
                  </td>
                  <td class="text-right">{{ qty(p.projected_qty) }} <span class="text-xs opacity-75">{{ p.uom }}</span></td>
                  <td class="text-right">{{ qty(p.projected_m3, 1) }}</td>
                  <td class="text-right">{{ qty(p.sold_12m) }}</td>
                  <td class="text-right">
                    <span :class="coverageClass(p.coverage_months)">{{ coverageText(p.coverage_months) }}</span>
                  </td>
                  <td class="text-right">{{ p.n_buyers }}</td>
                  <td class="text-xs">
                    <div v-for="b in p.top_buyers.slice(0, 3)" :key="b.idperson" class="pi-ellipsis">
                      {{ b.name }} <span class="opacity-50">({{ qty(b.qty) }})</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="!loading && !products.length" class="p-4 opacity-75">No product matches these filters.</div>
          </div>
        </vx-card>
      </div>

      <!-- Selected product -->
      <div class="vx-col w-full xl:w-2/5 mb-base">
        <vx-card v-if="current" :title="`${current.category} — ${current.product}`">
          <div class="text-sm mb-3">
            Projected <b>{{ qty(current.projected_qty) }} {{ current.uom }}</b> ({{ qty(current.projected_m3, 1) }} m³)
            · stock {{ qty(current.current_qty) }} · sold {{ qty(current.sold_12m) }} in 12 months
            · coverage {{ coverageText(current.coverage_months) }}
            <span v-if="current.open_quotes"> · {{ current.open_quotes }} pending quotation(s) this year</span>
            · <a :href="'/web/stock/' + current.idproduct" target="_blank" rel="noopener">stock page</a>
          </div>

          <div class="flex flex-wrap items-center mb-2">
            <h6 class="flex-grow mb-0">Clients who bought it the most</h6>
            <vs-button size="small" type="border" class="mr-2" @click="tickAll(true)">Tick all</vs-button>
            <vs-button size="small" type="border" @click="tickAll(false)">None</vs-button>
          </div>
          <table class="pi-table w-full mb-4">
            <thead>
              <tr>
                <th style="width: 40px"></th><th>Client</th><th>Country</th>
                <th class="text-right">Qty</th><th class="text-right">Invoices</th><th>Last</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="b in current.top_buyers" :key="b.idperson">
                <td>
                  <vs-checkbox :value="!!ticked[b.idperson]" :disabled="!canMail(b)" @input="tick(b, $event)" />
                </td>
                <td>
                  <a :href="'/web/person/' + b.idperson" target="_blank" rel="noopener">{{ b.name }}</a>
                  <span v-if="b.unsubscribed" class="pi-flag pi-flag--danger">unsubscribed</span>
                  <span v-else-if="!b.has_email" class="pi-flag pi-flag--danger">no e-mail</span>
                </td>
                <td>{{ b.country_code || '—' }} <span class="text-xs opacity-75">{{ (b.lang || '').toUpperCase() }}</span></td>
                <td class="text-right">{{ qty(b.qty) }}</td>
                <td class="text-right">{{ b.n_invoices }}</td>
                <td>{{ b.last_date }}</td>
              </tr>
            </tbody>
          </table>
          <div v-if="!current.top_buyers.length" class="opacity-75 mb-4">No buyer yet — use "All potential clients".</div>

          <div class="flex flex-wrap">
            <vs-button class="mr-2 mb-2" icon-pack="feather" icon="icon-mail" :disabled="!tickedCount || !isAdmin" @click="openComposer">
              Send an offer to {{ tickedCount }} client(s)
            </vs-button>
            <vs-button class="mb-2" type="border" icon-pack="feather" icon="icon-users" @click="clientsPopup = true">
              All potential clients (5 methods)
            </vs-button>
          </div>
          <div v-if="!isAdmin" class="text-sm opacity-75">Sending offers is reserved to administrators.</div>
        </vx-card>
        <vx-card v-else>
          <div class="opacity-75">Click a product to see its best buyers and send them an offer.</div>
        </vx-card>
      </div>
    </div>

    <offer-mail-composer v-if="composerOpen" :active.sync="composerOpen" :idproduct="current && current.idproduct"
      :selection="composerSelection" />
    <potential-clients-popup v-if="current" :active.sync="clientsPopup" :idproduct="current.idproduct" />
  </div>
</template>

<script>
import axios from '@/axios.js'
import vSelect from 'vue-select'
import OfferMailComposer from '@/components/potential-interest/OfferMailComposer.vue'
import PotentialClientsPopup from '@/components/potential-interest/PotentialClientsPopup.vue'

const RAW_MATERIAL_TYPE = 4 // « Biomass - bulk » : grumes, écorce, bois de chauffage en vrac

export default {
  name: 'PotentialInterest',
  components: { vSelect, OfferMailComposer, PotentialClientsPopup },
  data () {
    return {
      loading: false,
      error: null,
      products: [],
      types: [],
      categories: [],
      current: null,
      ticked: {},
      composerOpen: false,
      composerSelection: [],
      clientsPopup: false,
      filters: {
        sort: 'volume',
        types: [],
        categories: [],
        minVolume: 0,
        limit: 50,
        onlyWithBuyers: true,
        includeRaw: false
      }
    }
  },
  computed: {
    isAdmin () {
      return (localStorage.getItem('login-roles') || '').toLowerCase().includes('admin')
    },
    typeOptions () {
      return this.types.map(t => ({ value: t.id, label: t.name_en }))
    },
    categoryOptions () {
      const types = this.filters.types
      return this.categories
        .filter(c => !types.length || types.indexOf(c.idtype) !== -1)
        .map(c => ({ value: c.id, label: c.name_en }))
    },
    tickedCount () {
      return Object.keys(this.ticked).length
    }
  },
  watch: {
    'filters.onlyWithBuyers' () { this.load() },
    'filters.includeRaw' () { this.load() }
  },
  created () {
    const roles = (localStorage.getItem('login-roles') || '').toLowerCase()
    if (!roles.includes('admin')) {
      this.$vs.notify({ title: 'Denied', text: 'This page is reserved for administrators', color: 'danger', position: 'bottom-right' })
      this.$router.push({ path: '/dashboard/analytics' })
      return
    }
    this.load()
  },
  methods: {
    qty (value, digits = 0) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return n.toLocaleString('en-US', { maximumFractionDigits: digits })
    },
    coverageText (months) {
      if (months === null || months === undefined) return 'no sale in 12 m'
      if (months >= 120) return '> 10 years'
      if (months >= 24) return `${(months / 12).toFixed(1)} years`
      return `${Number(months).toFixed(1)} months`
    },
    coverageClass (months) {
      if (months === null || months === undefined || months >= 24) return 'text-danger font-semibold'
      if (months >= 6) return 'text-warning'
      return ''
    },
    canMail (b) {
      return b.has_email && !b.unsubscribed
    },
    async load () {
      this.loading = true
      this.error = null
      const f = this.filters
      const params = {
        sort: f.sort,
        limit: f.limit,
        top_buyers: 10,
        min_volume: f.minVolume || 0,
        only_with_buyers: f.onlyWithBuyers ? '1' : '0'
      }
      if (f.types.length) params.types = f.types.join(',')
      else params.exclude_types = f.includeRaw ? '' : String(RAW_MATERIAL_TYPE)
      if (f.categories.length) params.categories = f.categories.join(',')
      try {
        const { data } = await axios.get('/potential-interest/overstock', { params })
        this.products = data.products || []
        this.types = data.types || []
        this.categories = data.categories || []
        const again = this.current && this.products.find(p => p.idproduct === this.current.idproduct)
        if (again) this.current = again
        else if (this.products.length) this.pick(this.products[0])
        else this.current = null
      } catch (e) {
        const d = e.response && e.response.data
        this.error = (d && (d.message || d.detail)) || e.message || 'Could not load the products.'
      } finally {
        this.loading = false
      }
    },
    pick (p) {
      if (this.current && this.current.idproduct === p.idproduct) return
      this.current = p
      this.ticked = {}
      p.top_buyers.forEach(b => { if (this.canMail(b)) this.$set(this.ticked, b.idperson, true) })
    },
    tick (b, on) {
      if (on) this.$set(this.ticked, b.idperson, true)
      else this.$delete(this.ticked, b.idperson)
    },
    tickAll (on) {
      this.ticked = {}
      if (on) this.current.top_buyers.forEach(b => { if (this.canMail(b)) this.$set(this.ticked, b.idperson, true) })
    },
    openComposer () {
      this.composerSelection = Object.keys(this.ticked).map(id => ({ idperson: Number(id), reason: 'bought' }))
      this.composerOpen = true
    }
  }
}
</script>

<style lang="scss">
#potential-interest {
  .pi-scroll {
    overflow-x: auto;
    max-height: calc(100vh - 360px);
    overflow-y: auto;
  }

  .pi-table {
    border-collapse: collapse;
    font-size: 0.88rem;

    th,
    td {
      padding: 0.4rem 0.5rem;
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
      vertical-align: top;
    }

    th {
      position: sticky;
      top: 0;
      z-index: 1;
      background: #fff;
      text-align: left;
      font-weight: 600;
      text-transform: uppercase;
      font-size: 0.7rem;
      white-space: nowrap;
    }
  }

  .pi-product-row {
    cursor: pointer;

    &:hover {
      background: rgba(var(--vs-primary), 0.05);
    }
  }

  .pi-product-row--active {
    background: rgba(var(--vs-primary), 0.12) !important;
  }

  .pi-ellipsis {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .pi-input,
  .pi-select {
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 5px;
    padding: 0.45rem 0.5rem;
    font-size: 0.9rem;
    background: transparent;
    color: inherit;
  }

  .pi-flag {
    display: inline-block;
    font-size: 0.7rem;
    padding: 0 6px;
    margin-left: 4px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.08);
  }

  .pi-flag--danger {
    background: rgba(var(--vs-danger), 0.15);
    color: rgba(var(--vs-danger), 1);
  }
}

.theme-dark #potential-interest .pi-table th {
  background: #262c49;
}
</style>
